import { useMemo } from "react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip as RechartsTooltip } from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { InfoTooltip } from "@/lib/dashboard/InfoTooltip";
import { DONUT_COLORS } from "@/lib/dashboard/chartColors";
import { useI18n } from "@/hooks/useI18n";
import { useAgencyAdvisorMembers } from "@/hooks/useAgencyAdvisorMembers";
import { AgencyAdvisorMemberRow, AgencyDashboardPeriod } from "@/integrations/supabase/rpc-types";
import { Loader2, RefreshCw, Users, XCircle } from "lucide-react";
import type { TranslationKey } from "@/types/i18n.types";

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(value || 0);
}

function formatCount(value: number): string {
  return String(value);
}

function getRoleLabel(role: string, t: (key: TranslationKey) => string): string {
  switch (role) {
    case "travel_designer":
      return t("role.travelAdvisor");
    case "travel_advisor_member":
      return t("role.travelAdvisorMember");
    default:
      return t("role.unknown");
  }
}

function getAdvisorName(row: AgencyAdvisorMemberRow): string {
  return [row.first_name, row.last_name].filter(Boolean).join(" ") || row.advisor_id;
}

interface AdvisorPieChartCardProps {
  title: string;
  infoText: string;
  emptyText: string;
  rows: AgencyAdvisorMemberRow[];
  valueSelector: (row: AgencyAdvisorMemberRow) => number;
  formatValue: (value: number) => string;
}

function AdvisorPieChartCard({
  title,
  infoText,
  emptyText,
  rows,
  valueSelector,
  formatValue,
}: AdvisorPieChartCardProps) {
  const pieData = useMemo(
    () =>
      rows
        .map((row) => ({ name: getAdvisorName(row), value: valueSelector(row) }))
        .filter((d) => d.value > 0)
        .sort((a, b) => b.value - a.value),
    [rows, valueSelector]
  );

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          {title}
          <InfoTooltip text={infoText} />
        </CardTitle>
      </CardHeader>
      <CardContent>
        {pieData.length === 0 ? (
          <div className="flex items-center justify-center h-48 text-sm text-gray-400">
            {emptyText}
          </div>
        ) : (
          <div className="flex items-center gap-4">
            <div className="h-40 w-40 flex-shrink-0">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={45}
                    outerRadius={70}
                    paddingAngle={2}
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={entry.name} fill={DONUT_COLORS[index % DONUT_COLORS.length]} />
                    ))}
                  </Pie>
                  <RechartsTooltip formatter={(value: number) => formatValue(value)} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <ul className="space-y-1.5 text-sm flex-1 min-w-0">
              {pieData.map((d, index) => (
                <li key={d.name} className="flex items-center gap-2 min-w-0">
                  <span
                    className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: DONUT_COLORS[index % DONUT_COLORS.length] }}
                  />
                  <span className="truncate text-gray-700">{d.name}</span>
                  <span className="ml-auto text-gray-500 flex-shrink-0">{formatValue(d.value)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface AdvisorMembersTabProps {
  period: AgencyDashboardPeriod;
}

export function AdvisorMembersTab({ period }: AdvisorMembersTabProps) {
  const { t } = useI18n();
  const { data, loading, error, refresh } = useAgencyAdvisorMembers(period);

  if (loading) {
    return (
      <Card>
        <CardContent>
          <div className="flex items-center gap-2 py-10 text-gray-500 justify-center">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="text-sm">{t("dashboard.overview.advisors.loading")}</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <div className="flex flex-col items-center gap-3 py-12 text-center">
            <div className="flex items-center justify-center h-12 w-12 rounded-full bg-red-50 text-red-500">
              <XCircle className="h-6 w-6" />
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-gray-900">
                {t("dashboard.overview.errorTitle")}
              </p>
              <p className="text-sm text-gray-500">
                {t("dashboard.overview.errorDescription")}
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={() => refresh()} className="mt-1">
              <RefreshCw className="h-4 w-4 mr-2" />
              {t("dashboard.retry")}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (data.length === 0) {
    return (
      <Card>
        <CardContent>
          <div className="flex flex-col items-center gap-2 py-12 text-center text-gray-500">
            <Users className="h-8 w-8 text-gray-300" />
            <p className="text-sm">{t("dashboard.overview.advisors.empty")}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <AdvisorPieChartCard
          title={t("dashboard.overview.advisors.marginChartTitle")}
          infoText={t("dashboard.overview.advisors.marginChartInfo")}
          emptyText={t("dashboard.overview.advisors.marginChartEmpty")}
          rows={data}
          valueSelector={(row) => row.margin_total}
          formatValue={formatCurrency}
        />
        <AdvisorPieChartCard
          title={t("dashboard.overview.advisors.activeChartTitle")}
          infoText={t("dashboard.overview.advisors.activeChartInfo")}
          emptyText={t("dashboard.overview.advisors.activeChartEmpty")}
          rows={data}
          valueSelector={(row) => row.active_count}
          formatValue={formatCount}
        />
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("dashboard.overview.advisors.columnName")}</TableHead>
                  <TableHead>{t("dashboard.overview.advisors.columnRole")}</TableHead>
                  <TableHead className="text-right">
                    <span className="inline-flex items-center gap-1.5">
                      {t("dashboard.overview.advisors.columnActive")}
                      <InfoTooltip text={t("dashboard.overview.advisors.columnActiveInfo")} />
                    </span>
                  </TableHead>
                  <TableHead className="text-right">
                    <span className="inline-flex items-center gap-1.5">
                      {t("dashboard.overview.advisors.columnCompleted")}
                      <InfoTooltip text={t("dashboard.overview.advisors.columnCompletedInfo")} />
                    </span>
                  </TableHead>
                  <TableHead className="text-right">
                    <span className="inline-flex items-center gap-1.5">
                      {t("dashboard.overview.advisors.columnValue")}
                      <InfoTooltip text={t("dashboard.overview.advisors.columnValueInfo")} />
                    </span>
                  </TableHead>
                  <TableHead className="text-right">
                    <span className="inline-flex items-center gap-1.5">
                      {t("dashboard.overview.advisors.columnMargin")}
                      <InfoTooltip text={t("dashboard.overview.advisors.columnMarginInfo")} />
                    </span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.map((advisor) => (
                  <TableRow key={advisor.advisor_id}>
                    <TableCell className="font-medium text-gray-900">
                      {getAdvisorName(advisor)}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{getRoleLabel(advisor.role, t)}</Badge>
                    </TableCell>
                    <TableCell className="text-right">{advisor.active_count}</TableCell>
                    <TableCell className="text-right">{advisor.completed_count}</TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(advisor.closed_value_total)}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(advisor.margin_total)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default AdvisorMembersTab;
